import { Link, useNavigate } from "react-router-dom";
import { Trash2, ShoppingBag } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useCart } from "@/context/CartContext";
import { brl, num, imgUrl } from "@/lib/api";

function CartLine({ item, onRemove, onQty }) {
  return (
    <li className="flex flex-col gap-4 py-5 sm:flex-row sm:items-center" data-testid={`cart-item-${item.key}`}>
      <img src={imgUrl(item.image_url)} alt={item.name} className="h-20 w-20 rounded object-cover bg-slate-100" />
      <div className="flex-1">
        <p className="font-display font-bold">{item.name}</p>
        <p className="text-sm text-slate-500">
          {item.pricing_type === "m2"
            ? `${num(item.height, 1)} × ${num(item.width, 1)} cm = ${num(item.area, 4)} m²${item.minApplied ? ` (cobrado ${num(item.billedArea, 2)} m² mínimo)` : ""} · ${brl(item.price)}/m²`
            : item.option ? `${item.option_label}: ${item.option} · pacote com ${item.quantity} ${item.unit_label}(s)` : `${brl(item.price)}/${item.unit_label}`}
        </p>
      </div>
      <div className="flex items-center gap-3">
        {item.option
          ? <span className="w-20 text-center text-xs font-semibold text-slate-500" data-testid={`cart-package-${item.key}`}>1 pacote</span>
          : <Input type="number" min="1" value={item.quantity} onChange={(e) => onQty(item.key, e.target.value)} className="h-10 w-20 font-mono" data-testid={`cart-qty-${item.key}`} />}
        <p className="w-28 text-right font-mono font-bold">{brl(item.subtotal)}</p>
        <Button variant="ghost" size="icon" onClick={() => onRemove(item.key)} data-testid={`cart-remove-${item.key}`} aria-label="Remover">
          <Trash2 className="h-4 w-4 text-slate-400 hover:text-red-500" />
        </Button>
      </div>
    </li>
  );
}

export default function CartPage() {
  const { items, remove, updateQty, total } = useCart();
  const navigate = useNavigate();

  if (!items.length)
    return (
      <div className="mx-auto max-w-3xl px-4 py-24 text-center" data-testid="cart-empty">
        <ShoppingBag className="mx-auto h-10 w-10 text-slate-300" />
        <p className="mt-4 font-display text-xl font-bold">Seu carrinho está vazio</p>
        <Link to="/" className="mt-4 inline-block font-semibold text-sky-600">Ver produtos</Link>
      </div>
    );

  return (
    <div className="mx-auto max-w-5xl px-4 py-12 sm:px-6 lg:px-8">
      <h1 className="font-display text-3xl font-extrabold tracking-tight sm:text-4xl">Carrinho</h1>
      <ul className="mt-6 divide-y divide-slate-200 rounded-md border border-slate-200 bg-white px-5" data-testid="cart-list">
        {items.map((i) => <CartLine key={i.key} item={i} onRemove={remove} onQty={updateQty} />)}
      </ul>
      <div className="mt-6 flex flex-col items-end gap-4">
        <p className="text-sm text-slate-500">Total <span className="ml-3 font-mono text-3xl font-bold text-slate-900" data-testid="cart-total">{brl(total)}</span></p>
        <p className="text-xs text-slate-400">Retirada na loja: Rua Wenceslau Veiga, 01, Valéria, Salvador - BA.</p>
        <Button onClick={() => navigate("/checkout")} className="h-12 rounded-full bg-[#E5007D] px-8 font-bold hover:bg-[#c2006a]" data-testid="btn-checkout">
          Finalizar compra
        </Button>
      </div>
    </div>
  );
}
